/**
 * API Service
 * 
 * This service handles all API calls to the backend.
 * Currently uses dummy data, but structured to easily switch to real API calls.
 * 
 * To switch to real API:
 * 1. Set REACT_APP_USE_REAL_API=true
 * 2. Set REACT_APP_API_URL (optional, defaults to http://localhost:8000/api/v1)
 * 3. (Optional) Set REACT_APP_API_TOKEN or localStorage API_TEST_TOKEN
 * 4. All functions return the same data structure as dummy data
 */

// Import dummy data
import { DUMMY_HOT_TAGS, DUMMY_TAG_DETAILS, DUMMY_LATEST_MEDIA, DUMMY_MEDIA_DETAILS, DUMMY_REGIONS, DUMMY_PLATFORMS } from '../data/dummyData';

// Configuration
// - Set REACT_APP_USE_REAL_API=true to enable backend calls
// - If DISABLE_AUTH=true in api-service, no token is required
const USE_REAL_API = (process.env.REACT_APP_USE_REAL_API || '').toLowerCase() === 'true';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
const DEBUG_API = (process.env.REACT_APP_DEBUG_API || '').toLowerCase() === 'true';

/**
 * Default regions shown in UI for both Tags and Media (for now: global, sg, us, uk).
 * Used when API fails or returns empty; merged with API regions when available.
 */
export const DEFAULT_UI_REGIONS = [
  { region_code: 'GLOBAL', region_name: 'Global', region_scope: 'global' },
  { region_code: 'SG', region_name: 'Singapore', region_scope: 'country' },
  { region_code: 'US', region_name: 'United States', region_scope: 'country' },
  { region_code: 'GB', region_name: 'United Kingdom', region_scope: 'country' }
];

/** Region code -> display name for "Where this ranks" */
const REGION_DISPLAY_NAMES = {
  US: 'United States',
  GB: 'United Kingdom',
  UK: 'United Kingdom',
  SG: 'Singapore',
  JP: 'Japan',
  DE: 'Germany',
  FR: 'France',
  KR: 'South Korea',
  BR: 'Brazil',
  IN: 'India',
  AU: 'Australia',
  CA: 'Canada'
};

/**
 * Map backend "regions" array ({region, score, rank}) to region_ranks shape for UI.
 * @param {Array} regions - From merged rankings doc (regions breakdown)
 * @returns {Array} [{ region, region_code, region_name, rank, score }, ...]
 */
function normalizeRegionsToRanks(regions) {
  if (!Array.isArray(regions) || regions.length === 0) return [];
  return regions.map((r) => {
    const code = (r?.region || r?.region_code || '').toString().trim().toUpperCase();
    const name = REGION_DISPLAY_NAMES[code] || code || '—';
    return {
      region: code,
      region_code: code,
      region_name: name,
      rank: r?.rank ?? r?.overall_rank ?? 0,
      score: r?.score ?? 0
    };
  }).filter((r) => r.region);
}

/**
 * Merge API regions with default UI regions and sort (GLOBAL, SG, US, GB, then others).
 * @param {Array} apiRegions - Regions from fetchRegions()
 * @returns {Array} Deduplicated, sorted list for dropdowns
 */
export function mergeAndSortRegions(apiRegions) {
  const byCode = new Map();
  [...DEFAULT_UI_REGIONS, ...(Array.isArray(apiRegions) ? apiRegions : [])].forEach((r) => {
    const code = (r?.region_code || '').toString().trim().toUpperCase();
    if (!code) return;
    if (!byCode.has(code)) {
      byCode.set(code, { ...r, region_code: code, region_name: r?.region_name || code });
    }
  });
  const order = ['GLOBAL', 'SG', 'US', 'GB'];
  const pin = (code) => {
    const i = order.indexOf(code);
    return i >= 0 ? i : order.length;
  };
  return Array.from(byCode.values()).sort((a, b) => {
    const d = pin(a.region_code) - pin(b.region_code);
    if (d !== 0) return d;
    return (a.region_name || a.region_code).localeCompare(b.region_name || b.region_code);
  });
}

function normalizeYouTubeMediaItem(item) {
  if (!item || typeof item !== 'object') return item;

  const platform = (item.platform || '').toString().toLowerCase();
  const mediaUrl = (item.media_url || '').toString().toLowerCase();

  // If original source is a YouTube video, media_type should be "video".
  const isYouTubeVideo =
    platform === 'youtube' ||
    (mediaUrl.includes('youtube.com') && !mediaUrl.includes('music.youtube.com')) ||
    mediaUrl.includes('youtu.be');

  if (!isYouTubeVideo) return item;

  return {
    ...item,
    platform: 'youtube',
    media_type: 'video'
  };
}

/**
 * Normalize media detail from API (MCP envelope or legacy shapes).
 * Flattens content_analysis.analytics and content_analysis.learn_from_this to top level
 * so the UI can display all fields.
 * @param {Object} raw - Raw response (json.data or full json)
 * @returns {Object} Normalized media detail for MediaDetailView
 */
function normalizeMediaDetail(raw) {
  if (!raw || typeof raw !== 'object') return raw;

  const content = raw.content_analysis || {};
  // Structure contains hook, segments, pacing, call_to_action, duration_category
  const structure = content.structure || {};
  const segments = (structure.segments && Array.isArray(structure.segments)) 
    ? structure.segments 
    : (content.segments && Array.isArray(content.segments) ? content.segments : []);
  const strategy = content.content_strategy || {};
  // AI short summary: from content_analysis (generated with analysis), else fallback from main_topic + usp
  const aiSummaryShort = (content.short_summary && content.short_summary.trim()) ||
    [strategy.main_topic, strategy.usp].filter(Boolean).join(' ') ||
    content.summary ||
    '';
  return {
    ...raw,
    // Flatten content_analysis for UI
    analytics: raw.analytics || content.analytics || content,
    learn_from_this: raw.learn_from_this || content.learn_from_this,
    style_signals: raw.style_signals || content.style_signals,
    // Content breakdown from AI analysis (structure contains hook, segments, pacing, call_to_action, duration_category)
    content_breakdown: {
      hook: structure.hook || content.hook || '',
      segments,
      pacing: structure.pacing || content.pacing || '',
      call_to_action: structure.call_to_action || content.call_to_action || '',
      duration_category: structure.duration_category || content.duration_category || ''
    },
    // Keep raw content_analysis for any other fields
    content_analysis: content,
    // AI short summary (main_topic + usp) for media page "what it's about"
    ai_summary_short: aiSummaryShort,
    // Ensure metrics (API may use engagement or nest under metrics)
    metrics: raw.metrics || raw.engagement || {},
    region_ranks: raw.region_ranks || raw.region_rankings || normalizeRegionsToRanks(raw.regions) || [],
    source_tags: raw.source_tags || [],
    referenced_trending_tags: raw.referenced_trending_tags || raw.trending_tags || [],
    description: raw.description ?? content.summary ?? '',
    title: raw.title ?? '',
    media_url: raw.media_url ?? '',
    thumbnail_url: raw.thumbnail_url ?? '',
    platform: raw.platform ?? 'youtube',
    media_type: raw.media_type ?? 'video',
    trend_id: raw.trend_id || raw.id
  };
}

function normalizeMediaRankingsEnvelope(json) {
  // Expected shape: { mcp_version, context_type, data: { items: [...] }, ... }
  const items = json?.data?.items;
  if (!Array.isArray(items)) return json;
  return {
    ...json,
    data: {
      ...json.data,
      items: items.map(normalizeYouTubeMediaItem)
    }
  };
}

function debugLog(...args) {
  if (DEBUG_API && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}

async function debugFetch(url, options) {
  debugLog('[API] fetch', { url, options, USE_REAL_API, API_BASE_URL });
  const res = await fetch(url, options);
  debugLog('[API] response', { url, status: res.status, ok: res.ok });
  return res;
}

// Log config once at module load (helps confirm env is picked up)
debugLog('[API] config', {
  USE_REAL_API,
  API_BASE_URL,
  hasToken:
    !!(
      process.env.REACT_APP_API_TOKEN ||
      localStorage.getItem('API_TEST_TOKEN') ||
      localStorage.getItem('API_TOKEN')
    )
});

function getAuthHeaders() {
  // Optional auth: allow running with DISABLE_AUTH=true on the API.
  const token =
    process.env.REACT_APP_API_TOKEN ||
    localStorage.getItem('API_TEST_TOKEN') ||
    localStorage.getItem('API_TOKEN') ||
    '';

  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Fetch hot trending tags
 * @param {Object} params - Query parameters
 * @param {string} params.region_code - Region code (default: "GLOBAL")
 * @param {string} params.platform - Platform filter (default: "all")
 * @param {string} params.media_type - Media type filter (default: "all")
 * @param {string} params.time_window - Time window (default: "24h")
 * @param {string} params.sort_by - Sort by field (default: "score")
 * @param {number} params.limit - Number of results (default: 50)
 * @returns {Promise<Object>} Hot tags response
 */
export async function fetchHotTags(params = {}, options = {}) {
  const {
    region_code = 'GLOBAL',
    platform = 'all',
    media_type = 'all',
    time_window = '24h',
    sort_by = 'score',
    limit = 50
  } = params;

  if (USE_REAL_API) {
    const queryParams = new URLSearchParams({
      region_code,
      platform,
      media_type,
      time_window,
      sort_by,
      limit: limit.toString()
    });
    // Backend route is /api/v1/tags/trending
    const response = await debugFetch(`${API_BASE_URL}/tags/trending?${queryParams}`, {
      headers: {
        ...getAuthHeaders(),
      },
      signal: options.signal  // Support AbortController
    });
    if (!response.ok) throw new Error('Failed to fetch hot tags');
    const json = await response.json();
    // API returns MCP envelope; UI expects { tags: [...] }
    return json?.data || json;
  }

  // Return dummy data with simulated delay
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      // Check if request was aborted
      if (options.signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      let tags = [...DUMMY_HOT_TAGS.tags];
      
      // Apply sorting
      if (sort_by === 'score') {
        tags.sort((a, b) => b.trending_score - a.trending_score);
      } else if (sort_by === 'frequency') {
        tags.sort((a, b) => b.trend_count - a.trend_count);
      } else if (sort_by === 'growth') {
        tags.sort((a, b) => b.trend_lifecycle.velocity - a.trend_lifecycle.velocity);
      }

      // Apply limit
      tags = tags.slice(0, limit);

      resolve({
        ...DUMMY_HOT_TAGS,
        tags
      });
    }, 300);
    
    // Cleanup timeout if request is aborted
    if (options.signal) {
      options.signal.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        reject(new DOMException('Aborted', 'AbortError'));
      });
    }
  });
}

/**
 * Fetch tag detail.
 * @param {string} tagId - Tag ID or tag name
 * @param {Object} params - Query parameters
 * @param {string} params.region_code - Region code (default: "GLOBAL"). If provided, fetches tag for that specific region. If omitted, API returns tag from any region.
 * @param {boolean} params.include_lifecycle - Include lifecycle data (default: true)
 * @param {boolean} params.include_content_guide - Include content guidance (default: true)
 * @param {boolean} params.include_history - Include history (default: true)
 * @returns {Promise<Object>} Tag detail response
 */
export async function fetchTagDetail(tagId, params = {}) {
  const {
    region_code = 'GLOBAL',  // Default to GLOBAL, but can be overridden
    include_lifecycle = true,
    include_content_guide = true,
    include_history = true
  } = params;

  if (USE_REAL_API) {
    const queryParams = new URLSearchParams({
      include_lifecycle: include_lifecycle.toString(),
      include_content_guide: include_content_guide.toString(),
      include_history: include_history.toString()
    });
    
    // Add region_code if provided (API will use it to fetch tag for specific region)
    if (region_code) {
      queryParams.append('region_code', region_code);
    }
    
    const response = await debugFetch(`${API_BASE_URL}/tags/${encodeURIComponent(tagId)}?${queryParams}`, {
      headers: {
        ...getAuthHeaders(),
      }
    });
    if (!response.ok) throw new Error('Failed to fetch tag detail');
    const json = await response.json();
    return json?.data || json;
  }

  // Return dummy data
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      const tagDetail = DUMMY_TAG_DETAILS[tagId];
      if (tagDetail) {
        resolve(tagDetail);
      } else {
        // Return first tag detail as fallback
        resolve(Object.values(DUMMY_TAG_DETAILS)[0]);
      }
    }, 300);
  });
}

/**
 * Fetch latest media by platform
 * @param {Object} params - Query parameters
 * @param {string} params.region_code - Region code (default: "GLOBAL")
 * @param {string} params.platform - Platform (required)
 * @param {string} params.media_type - Media type filter (default: "all")
 * @param {number} params.limit - Number of results (default: 50)
 * @param {string} params.sort_by - Sort by field (default: "engagement")
 * @returns {Promise<Object>} Latest media response
 */
export async function fetchLatestMedia(params = {}) {
  const {
    region_code = 'GLOBAL',
    platform = 'all',
    media_type = 'all',
    limit = 50,
    sort_by = 'engagement'
  } = params;

  if (USE_REAL_API) {
    const queryParams = new URLSearchParams({
      region_code,
      platform,
      media_type,
      limit: limit.toString(),
      sort_by
    });
    const response = await debugFetch(`${API_BASE_URL}/media/latest?${queryParams}`, {
      headers: {
        ...getAuthHeaders(),
      }
    });
    if (!response.ok) throw new Error('Failed to fetch latest media');
    const json = await response.json();
    // Some endpoints return MCP envelope; keep compatibility with older callers
    return json?.data || json;
  }

  // Return dummy data
  return new Promise((resolve) => {
    setTimeout(() => {
      let result = { ...DUMMY_LATEST_MEDIA };
      
      // Filter by platform
      if (platform !== 'all') {
        result.items = result.items.filter(item => item.platform === platform);
        result.selected_platform = {
          platform,
          platform_name: DUMMY_PLATFORMS.find(p => p.id === platform)?.name || platform,
          total_items: result.items.length,
          last_updated: new Date().toISOString()
        };
      }

      // Filter by media type
      if (media_type !== 'all') {
        result.items = result.items.filter(item => item.media_type === media_type);
      }

      // Apply sorting
      if (sort_by === 'engagement') {
        result.items.sort((a, b) => (b.metrics.views || 0) - (a.metrics.views || 0));
      } else if (sort_by === 'recency') {
        result.items.sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
      }

      // Apply limit
      result.items = result.items.slice(0, limit);
      result.filters = { platform, media_type };

      resolve(result);
    }, 300);
  });
}

/**
 * Fetch ranked media list (recommended for Step 1 outputs)
 * Backend: GET /api/v1/media/rankings (Firestore media_rankings)
 *
 * @param {Object} params
 * @param {string} params.region - Region code (default: "GLOBAL")
 * @param {string} params.platform - Platform filter (default: "youtube")
 * @param {string} params.media_type - Media type filter (default: "video")
 * @param {number} params.limit - Number of results (default: 30)
 * @param {number} params.offset - Offset (default: 0)
 */
export async function fetchMediaRankings(params = {}, options = {}) {
  const {
    region = 'GLOBAL',
    platform = 'youtube',
    media_type = 'video',
    limit = 30,
    offset = 0
  } = params;

  if (USE_REAL_API) {
    // API accepts both region and region_code for rankings
    const queryParams = new URLSearchParams({
      region: region || 'GLOBAL',
      region_code: region || 'GLOBAL',
      limit: limit.toString(),
      offset: offset.toString()
    });
    queryParams.set('platform', platform);
    queryParams.set('media_type', media_type);

    const response = await debugFetch(`${API_BASE_URL}/media/rankings?${queryParams}`, {
      headers: {
        ...getAuthHeaders(),
      },
      signal: options.signal  // Support AbortController
    });
    if (!response.ok) throw new Error('Failed to fetch media rankings');
    const json = await response.json();
    return normalizeMediaRankingsEnvelope(json);
  }

  // Dummy fallback: reuse latest media list and pretend it is ranked
  const dummy = await fetchLatestMedia({ region_code: region, platform, media_type, limit, sort_by: 'engagement' });
  const items = (dummy.items || []).map((it, idx) => ({ ...it, rank: idx + 1, score: (it.metrics?.views || 0) }));
  return {
    mcp_version: '1.0',
    context_type: 'media_rankings',
    data: { items, count: items.length, region, platform, media_type },
    metadata: { source: 'dummy' }
  };
}

/**
 * Fetch media detail
 * @param {string} trendId - Trend ID
 * @param {Object} params - Query parameters
 * @param {string} params.region_code - Region code (default: "GLOBAL")
 * @param {boolean} params.include_analytics - Include analytics (default: true)
 * @param {boolean} params.include_learn_from - Include "Learn From This" (default: true)
 * @returns {Promise<Object>} Media detail response
 */
export async function fetchMediaDetail(trendId, params = {}) {
  const {
    region_code = 'GLOBAL',
    include_analytics = true,
    include_learn_from = true
  } = params;

  if (USE_REAL_API) {
    // API accepts both region and region_code for media detail
    const queryParams = new URLSearchParams({
      region: region_code,
      region_code,
      include_analytics: include_analytics.toString(),
      include_learn_from: include_learn_from.toString()
    });
    const response = await debugFetch(`${API_BASE_URL}/media/${trendId}?${queryParams}`, {
      headers: {
        ...getAuthHeaders(),
      }
    });
    if (!response.ok) throw new Error('Failed to fetch media detail');
    const json = await response.json();
    const data = json?.data ?? json;
    return normalizeMediaDetail(normalizeYouTubeMediaItem(data));
  }

  // Return dummy data
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      const mediaDetail = DUMMY_MEDIA_DETAILS[trendId];
      if (mediaDetail) {
        resolve(mediaDetail);
      } else {
        // Return first media detail as fallback
        resolve(Object.values(DUMMY_MEDIA_DETAILS)[0]);
      }
    }, 300);
  });
}

/**
 * Fetch available regions
 * @returns {Promise<Array>} List of regions
 */
export async function fetchRegions() {
  if (USE_REAL_API) {
    // Backend route is /api/v1/tags/regions
    const response = await debugFetch(`${API_BASE_URL}/tags/regions`, {
      headers: {
        ...getAuthHeaders(),
      }
    });
    if (!response.ok) throw new Error('Failed to fetch regions');
    const json = await response.json();
    // API returns MCP envelope: { data: { regions: [...] } }
    return json?.data?.regions || json?.regions || json;
  }

  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(DUMMY_REGIONS);
    }, 100);
  });
}

/**
 * Fetch available platforms
 * @returns {Promise<Array>} List of platforms
 */
export async function fetchPlatforms() {
  // Supported platforms in frontend (TikTok hidden for now)
  const SUPPORTED_PLATFORMS = ['youtube'];
  
  if (USE_REAL_API) {
    const response = await debugFetch(`${API_BASE_URL}/platforms`, {
      headers: {
        ...getAuthHeaders(),
      }
    });
    if (!response.ok) throw new Error('Failed to fetch platforms');
    const json = await response.json();
    // Filter to supported platforms (YouTube only for now)
    return (Array.isArray(json) ? json : []).filter((p) => SUPPORTED_PLATFORMS.includes(p?.id));
  }

  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(DUMMY_PLATFORMS.filter((p) => SUPPORTED_PLATFORMS.includes(p?.id)));
    }, 100);
  });
}

const apiService = {
  fetchHotTags,
  fetchTagDetail,
  fetchLatestMedia,
  fetchMediaRankings,
  fetchMediaDetail,
  fetchRegions,
  fetchPlatforms
};

export default apiService;
